import { v4 as uuidv4 } from 'uuid'
import {
    CreateCartItemCommand2,
    useCreateCartItemsMutation,
    useDeleteCartItemsByIdMutation,
} from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { PromiseResponse } from 'helpers/types'
import { ErrorField, handleError } from 'helpers/errors'
import { useContext } from 'react'
import { GuestsDataContext } from './guestsDataProvider'
import { useCart } from 'store/cart-slice/hooks'
import { useAppSelector } from 'store/hooks'
import { MarchVoucher } from 'store/api-slice/types'

export const useGuestsCart = (): {
    cartItems: MarchVoucher[]
    addItemsToCart: () => Promise<PromiseResponse>
    deleteItemsFromCart: () => Promise<PromiseResponse>
    verifyMarchPromoTicketsIds: () => boolean
    isSubmitLoading: boolean
    isSubmitSuccess: boolean
    isSubmitError: boolean
    isDeleteLoading: boolean
    isDeleteSuccess: boolean
    isDeleteError: boolean
    isEditLoading: boolean
    isEditSuccess: boolean
    isEditError: boolean
} => {
    const { language } = useApp()
    const { refetchCart, cartCreatorType } = useCart()

    const { tickets, marchVouchers: cartItems } = useAppSelector(
        (state) => state.cart.items
    )

    const { marchVouchersData, setMarchVouchersData } =
        useContext(GuestsDataContext)

    const [postCartItems, postStatus] = useCreateCartItemsMutation()
    const {
        isLoading: isSubmitLoading,
        isSuccess: isSubmitSuccess,
        isError: isSubmitError,
    } = postStatus

    const [deleteCartItems, deleteStatus] = useDeleteCartItemsByIdMutation()
    const {
        isLoading: isDeleteLoading,
        isSuccess: isDeleteSuccess,
        isError: isDeleteError,
    } = deleteStatus

    const isEditLoading = isDeleteLoading || isSubmitLoading
    const isEditSuccess = isDeleteSuccess && isSubmitSuccess
    const isEditError = isDeleteError || isSubmitError

    const validateInputValues = () => {
        // TODO: figure out some better solution -> react-form-hooks
        const validatedData = marchVouchersData.map((data) => {
            const { firstName, lastName } = data
            const isFirstNameError =
                !firstName || !/^([^0-9]{2,})$/.test(firstName)
            const isLastNameError =
                !lastName || !/^([^0-9]{2,})$/.test(lastName)

            return {
                ...data,
                isFirstNameError,
                isLastNameError,
            }
        })
        const hasErrors = validatedData.some(
            ({ isFirstNameError, isLastNameError }) =>
                isFirstNameError || isLastNameError
        )

        setMarchVouchersData(validatedData)
        return !hasErrors
    }

    const addItemsToCart = async () => {
        if (!validateInputValues()) return { isSuccess: false }

        const cartItemsCommandData = marchVouchersData
            .map((data) => {
                const { lastName, firstName, productId, tickets } = data

                const cartItemDataByTickets: CreateCartItemCommand2[] =
                    tickets.map(({ ticketId }) => ({
                        cartItemId: uuidv4(),
                        productId: productId || '',
                        productType:
                            'voucher' as CreateCartItemCommand2['productType'],
                        quantity: 1,
                        detail: {
                            voucherType: 'next-visit',
                            cartItemId: ticketId || '',
                            name: firstName?.trim(),
                            surname: lastName?.trim(),
                            type: 'voucher',
                        },
                    }))

                return cartItemDataByTickets
            })
            .flat()

        // remove items already added to cart
        if (cartItems.length) {
            try {
                const response = await deleteItemsFromCart(false)
                handleError(response)
            } catch (errors: unknown) {
                const { messages } = errors as {
                    messages: ErrorField[]
                }
                return { isSuccess: false, messages }
            }
        }

        try {
            const response = await postCartItems({
                createCartItemsCommand: {
                    items: [...cartItemsCommandData],
                },
                cartCreatorType,
                _locale: language,
            })
            handleError(response)
            refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const deleteItemsFromCart = async (shouldRefetchCart = true) => {
        const cartItemsIds = cartItems.map(({ cartItemId }) => cartItemId || '')
        const filteredCartItemsIds = cartItemsIds.filter((id) => id !== '')

        if (!filteredCartItemsIds.length) return { isSuccess: true }

        try {
            handleError(
                await deleteCartItems({
                    _locale: language,
                    cartCreatorType,
                    deleteCartItemsByIdCommand: {
                        cartItemUuids: [...filteredCartItemsIds],
                    },
                })
            )
            shouldRefetchCart && refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const verifyMarchPromoTicketsIds = () => {
        const availableTicketsIds = tickets
            ?.filter(
                ({ itemsBundle, detail: { person } }) =>
                    person !== 'kid_to_90_cm' && !itemsBundle?.length
            )
            .map(({ cartItemId }) => cartItemId)
            .filter((id) => !!id)
            .sort()
            .join()
        const marchVouchersIds = cartItems
            .map(({ detail: { cartItemId } }) => cartItemId)
            .sort()
            .join()

        return !marchVouchersIds || availableTicketsIds === marchVouchersIds
    }

    return {
        cartItems,
        addItemsToCart,
        deleteItemsFromCart,
        verifyMarchPromoTicketsIds,
        isSubmitLoading,
        isSubmitSuccess,
        isSubmitError,
        isDeleteLoading,
        isDeleteSuccess,
        isDeleteError,
        isEditLoading,
        isEditSuccess,
        isEditError,
    }
}
